import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Mail, ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";
import SectionReveal from "@/components/SectionReveal";
import useSeo from "@/hooks/useSeo";
import { api, formatApiErrorDetail } from "@/lib/api";
import { CATEGORIES, getFaqJsonLd } from "@/data/faqs";

function FaqItem({ item, isOpen, onToggle, testId }) {
  return (
    <div
      className={`border-b border-hairline/70 transition-colors ${isOpen ? "bg-cream-soft/40" : ""}`}
      data-testid={testId}
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-start justify-between gap-6 text-left py-6 md:py-7 px-1 group"
        aria-expanded={isOpen}
      >
        <span
          className={`font-serif-display text-lg md:text-xl leading-snug transition-colors ${
            isOpen ? "text-sage" : "text-ink group-hover:text-sage"
          }`}
        >
          {item.q}
        </span>
        <span
          className={`flex-shrink-0 mt-1 w-9 h-9 inline-flex items-center justify-center rounded-full border transition-all ${
            isOpen
              ? "border-sage bg-sage text-cream rotate-180"
              : "border-hairline text-ink-soft group-hover:border-sage group-hover:text-sage"
          }`}
        >
          <ChevronDown size={16} strokeWidth={1.8} />
        </span>
      </button>
      {isOpen && (
        <div className="pb-7 md:pb-8 pr-12 md:pr-16 px-1 -mt-1">
          {item.a.split("\n\n").map((para, i) => (
            <p key={i} className="text-ink-soft leading-relaxed mb-4 last:mb-0">
              {para}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

export default function FAQ() {
  useSeo({
    title: "FAQ · Pip Bennett — Working with me, coaching, hypnotherapy and more",
    description:
      "Honest answers to common questions about working with Pip Bennett — clinical hypnotherapy, coaching, menopause, midlife, logistics and the ethical scope of the work.",
    canonical: "https://pipbennett.com/faq",
    jsonLd: getFaqJsonLd(),
  });

  // Track open items as a Set of "categoryId-index"
  const [openKeys, setOpenKeys] = useState(new Set());
  const [activeCategory, setActiveCategory] = useState(CATEGORIES[0].id);
  const sectionRefs = useRef({});

  const toggle = (key) => {
    setOpenKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const jumpToCategory = (id) => {
    setActiveCategory(id);
    const el = sectionRefs.current[id];
    if (el) {
      // Allow space for sticky header
      const top = el.getBoundingClientRect().top + window.scrollY - 110;
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  // Track scroll position to highlight active category
  useEffect(() => {
    const onScroll = () => {
      const scrollY = window.scrollY + 180;
      let current = CATEGORIES[0].id;
      for (const cat of CATEGORIES) {
        const el = sectionRefs.current[cat.id];
        if (el && el.offsetTop <= scrollY) current = cat.id;
      }
      setActiveCategory(current);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div data-testid="page-faq">
      {/* HERO */}
      <section className="pt-12 md:pt-20 pb-12 md:pb-16 bg-gradient-to-b from-cream to-cream-soft border-b border-hairline">
        <div className="max-w-5xl mx-auto px-6 md:px-10 lg:px-14">
          <SectionReveal>
            <div className="eyebrow mb-5 text-gold-dark">FAQ</div>
            <h1
              className="font-serif-display text-5xl md:text-6xl lg:text-[4.5rem] text-ink leading-[1.05] max-w-3xl"
              data-testid="faq-title"
            >
              Honest answers to the <em className="text-sage italic">questions that come up</em>.
            </h1>
            <p className="mt-7 max-w-2xl text-lg text-ink-soft leading-relaxed">
              Practical, grounded answers about working with me — how the sessions work, who this is
              for, what hypnotherapy actually feels like, and where the boundaries of my care lie.
              If your question isn't here, you're warmly invited to{" "}
              <Link to="/contact" className="text-sage hover:text-sage-dark underline underline-offset-4">
                send it through directly
              </Link>
              .
            </p>
          </SectionReveal>
        </div>
      </section>

      {/* CONTENT */}
      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
            {/* Sticky sidebar nav */}
            <aside className="lg:col-span-3" data-testid="faq-sidebar">
              <div className="lg:sticky lg:top-28">
                <div className="eyebrow text-ink-soft mb-4">Browse by topic</div>
                <nav className="space-y-1.5 -mx-2">
                  {CATEGORIES.map((cat) => (
                    <button
                      key={cat.id}
                      type="button"
                      onClick={() => jumpToCategory(cat.id)}
                      data-testid={`faq-nav-${cat.id}`}
                      className={`block w-full text-left px-3 py-2.5 rounded-lg text-sm font-medium transition ${
                        activeCategory === cat.id
                          ? "bg-sage/8 text-sage"
                          : "text-ink-soft hover:text-ink hover:bg-cream-soft"
                      }`}
                    >
                      {cat.label}
                      <span className="ml-2 text-[0.7rem] text-ink-soft/60 font-normal">
                        ({cat.items.length})
                      </span>
                    </button>
                  ))}
                </nav>
              </div>
            </aside>

            {/* FAQ sections */}
            <div className="lg:col-span-9 space-y-16 md:space-y-20">
              {CATEGORIES.map((cat) => (
                <section
                  key={cat.id}
                  id={cat.id}
                  ref={(el) => (sectionRefs.current[cat.id] = el)}
                  data-testid={`faq-section-${cat.id}`}
                  className="scroll-mt-28"
                >
                  <SectionReveal>
                    <div className="eyebrow mb-3 text-sage">{cat.eyebrow}</div>
                    <h2 className="font-serif-display text-3xl md:text-4xl text-ink leading-tight mb-2">
                      {cat.label}
                    </h2>
                    <p className="text-sm text-ink-soft/70 mb-6">
                      {cat.items.length} question{cat.items.length === 1 ? "" : "s"}
                    </p>
                  </SectionReveal>

                  <div className="border-t border-hairline/70">
                    {cat.items.map((item, i) => {
                      const key = `${cat.id}-${i}`;
                      return (
                        <FaqItem
                          key={key}
                          item={item}
                          isOpen={openKeys.has(key)}
                          onToggle={() => toggle(key)}
                          testId={`faq-item-${cat.id}-${i}`}
                        />
                      );
                    })}
                  </div>
                </section>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ASK A QUESTION */}
      <AskAQuestion />
    </div>
  );
}

function AskAQuestion() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [question, setQuestion] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setError("");
    setLoading(true);
    try {
      await api.post("/faq/question", { name, email, question });
      setDone(true);
      toast.success("Thank you — your question has landed safely.");
    } catch (err) {
      const msg =
        formatApiErrorDetail(err?.response?.data?.detail) ||
        "Something went wrong. Could you try sending that again?";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      className="py-20 md:py-28 bg-cream-soft border-t border-hairline/60"
      data-testid="faq-ask-section"
    >
      <div className="max-w-3xl mx-auto px-6 md:px-10">
        <SectionReveal>
          <div className="inline-flex items-center gap-2 eyebrow text-sage mb-5">
            <Mail size={13} strokeWidth={1.8} />
            Still have a question?
          </div>
          <h2 className="font-serif-display text-4xl md:text-5xl text-ink leading-tight">
            You're warmly invited <em className="italic text-sage">to ask</em>.
          </h2>
          <p className="mt-7 text-lg text-ink-soft leading-relaxed max-w-2xl">
            If there's something you're wondering about, you're welcome to reach out here.
            Whether you're unsure where to begin, curious about how the work fits your situation,
            or simply wanting clarity before taking the next step — you're invited to ask.
          </p>
          <p className="mt-4 text-sm text-ink-soft/70 max-w-2xl leading-relaxed">
            Every message comes directly to Pip. There's no automation here, no funnel —
            just a quiet, considered reply when she's next at her desk.
          </p>
        </SectionReveal>

        <SectionReveal>
          {done ? (
            <div
              className="mt-12 bg-cream border border-sage/20 rounded-2xl p-8 md:p-10 flex items-start gap-5"
              data-testid="faq-ask-success"
            >
              <span className="flex-shrink-0 w-11 h-11 inline-flex items-center justify-center rounded-full bg-sage text-cream">
                <Check size={18} strokeWidth={1.8} />
              </span>
              <div>
                <div className="font-serif-display text-2xl text-ink leading-snug">
                  Thank you — your question has landed safely.
                </div>
                <p className="mt-3 text-ink-soft leading-relaxed">
                  Pip reads every message herself. She'll be in touch personally when she's
                  next at her desk. In the meantime, take a slow breath. You're in good hands.
                </p>
              </div>
            </div>
          ) : (
            <form
              onSubmit={submit}
              className="mt-12 space-y-6"
              data-testid="faq-ask-form"
              noValidate
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <label className="block">
                  <span className="block text-xs uppercase tracking-[0.18em] text-ink-soft/70 mb-2">
                    Your first name
                  </span>
                  <input
                    required
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. Alex"
                    className="w-full bg-cream border border-hairline rounded-xl px-5 py-3.5 text-ink placeholder-ink-soft/40 focus:outline-none focus:border-sage focus:ring-1 focus:ring-sage/20 transition"
                    data-testid="faq-ask-name"
                  />
                </label>
                <label className="block">
                  <span className="block text-xs uppercase tracking-[0.18em] text-ink-soft/70 mb-2">
                    Where shall Pip reply?
                  </span>
                  <input
                    required
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@yours.com"
                    className="w-full bg-cream border border-hairline rounded-xl px-5 py-3.5 text-ink placeholder-ink-soft/40 focus:outline-none focus:border-sage focus:ring-1 focus:ring-sage/20 transition"
                    data-testid="faq-ask-email"
                  />
                </label>
              </div>

              <label className="block">
                <span className="block text-xs uppercase tracking-[0.18em] text-ink-soft/70 mb-2">
                  What would you like to ask?
                </span>
                <textarea
                  required
                  rows={6}
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  placeholder="Take your time — there's no wrong way to ask."
                  className="w-full bg-cream border border-hairline rounded-xl px-5 py-4 text-ink placeholder-ink-soft/40 focus:outline-none focus:border-sage focus:ring-1 focus:ring-sage/20 transition resize-none leading-relaxed"
                  data-testid="faq-ask-question"
                />
              </label>

              {error && (
                <p
                  className="text-sm text-red-600/80"
                  role="alert"
                  data-testid="faq-ask-error"
                >
                  {error}
                </p>
              )}

              <div className="flex flex-col sm:flex-row sm:items-center gap-5 pt-2">
                <button
                  type="submit"
                  disabled={loading}
                  className="inline-flex items-center justify-center gap-2 bg-sage hover:bg-sage-dark text-cream rounded-full px-9 py-4 font-medium tracking-wide transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="faq-ask-submit"
                >
                  {loading ? "Sending…" : "Send your question"}
                  {!loading && <ArrowRight size={15} strokeWidth={1.8} />}
                </button>
                <p className="text-xs text-ink-soft/60 leading-relaxed max-w-sm">
                  Your message comes directly to Pip's inbox. No mailing list,
                  no marketing — only a thoughtful reply.
                </p>
              </div>
            </form>
          )}
        </SectionReveal>
      </div>
    </section>
  );
}
